import { DownloadOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Row, Table } from "antd";
import { useFormik } from "formik";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/functional_components/Button/PFButton";
import Filter from "../../../components/functional_components/Filter/Filter";
import { setFilters } from "../../../redux/ducks/Filters";
import { setLoading } from "../../../redux/ducks/Loading";
import User from "../../../services/api/User";
import { paginationDefaults } from "../../../utils/properties";
import { buildQueryString, exportFile, showError, showSuccess } from "../../../utils/utilities";

const InternalUsersList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      pageSize: paginationDefaults.size,
      totalItems: paginationDefaults.total,
    },
    downloadLoading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString(filters.obj, {});
    dispatch(setFilters(queryString));
    getInternalList(queryString.string);
  }, []);

  const columns = [
    { title: t("userInternal.cid"), dataIndex: "cid", key: "cid" },
    {
      title: t("userInternal.name"),
      dataIndex: "name",
      key: "name",
      responsive: ["md"],
    },
    {
      title: t("userInternal.surname"),
      dataIndex: "surname",
      key: "surname",
      responsive: ["md"],
    },
    {
      title: t("userInternal.roles"),
      dataIndex: "roles",
      key: "roles",
      render: (roles) => {
        return roles.map((role, idx) => {
          const separator = idx !== roles.length - 1 ? ", " : "";
          return role ? t(`roles.${role}`) + separator : null;
        });
      },
    },
    {
      title: t("roles.TIMESHEET_SUBSTITUTE_title"),
      dataIndex: "substituteName",
      key: "substitute",
      responsive: ["md"],
    },
    {
      title: t("roles.EXPENSE_REFUND_SUBST_title"),
      dataIndex: "expenseRefundSubstituteName",
      key: "expenseRefundSubstitute",
      responsive: ["md"],
    },
    {
      title: t("general.actions"),
      dataIndex: "",
      key: "x",
      width: 80,
      render: (record) => {
        return (
          <div className="space-around">
            <div onClick={() => goToInternalDetail(record)}>
              <EditOutlined className="icon-medium" />
            </div>
          </div>
        );
      },
    },
  ];

  const goToInternalDetail = (selectedInternal) => {
    navigate(`${selectedInternal ? selectedInternal.username : "new-internal"}`);
  };

  const getInternalList = async (queryString) => {
    dispatch(setLoading(true));
    const usersInternalList = await User.getUsersInternalList(queryString);
    if (!usersInternalList.error && usersInternalList.data) {
      usersInternalList.data.forEach((currentInternal, idx) => {
        currentInternal.key = idx;
        currentInternal.substituteName = currentInternal.substitute?.name
          ? `${currentInternal.substitute?.name} ${currentInternal.substitute?.surname}`
          : "";
        currentInternal.expenseRefundSubstituteName = currentInternal.expenseRefundSubstitute?.name
          ? `${currentInternal.expenseRefundSubstitute?.name} ${currentInternal.expenseRefundSubstitute?.surname}`
          : "";
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: usersInternalList.data,
        pagination: {
          currentPage: usersInternalList.currentPage ? usersInternalList.currentPage + 1 : paginationDefaults.page,
          pageSize: get(usersInternalList, "pageSize", paginationDefaults.size),
          totalItems: get(usersInternalList, "totalItems", paginationDefaults.totalItems),
        },
      }));
    } else {
      showError(get(usersInternalList, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const exportInternalsCsv = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const exportResponse = await User.exportInternalUsersCsv();
    if (!exportResponse.error) {
      const blob = new Blob([exportResponse]);
      exportFile(blob, "internals.csv");
      showSuccess();
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  const formikFilter = useFormik({
    initialValues: {
      surname: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      // When I search, I start my page to 0
      values["page"] = 0;
      const queryString = buildQueryString(filters.obj, values);
      dispatch(setFilters(queryString));
      getInternalList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "input",
      isRequired: false,
      id: "surname",
      name: "surname",
      value: formikFilter.values.surname,
      label: t("userInternal.surname"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString({ page: paginationDefaults.page - 1, size: paginationDefaults.size }, {});
    dispatch(setFilters(queryString));
    getInternalList(queryString.string);
  };

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => goToInternalDetail()}
          className="mr-10"
        />
        <Button
          isLoading={state.downloadLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={() => exportInternalsCsv()}
        />
      </Row>
      <Table
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.pageSize,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            getInternalList(queryString.string);
          },
        }}
        columns={columns}
        dataSource={state.tableContent}
        size="small"
      />
    </>
  );
};

export default InternalUsersList;
